import React from 'react';
import { ConfigProvider, Typography, theme } from 'antd';

const { Text } = Typography;

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}

/**
 * Compact replacement for antd's default `<Empty>` illustration -- a small
 * Tabler icon in a circle, a short title, one line of context, and an
 * optional CTA. Used for every empty/not-found state in the app instead of
 * the oversized generic mailbox image.
 *
 * The CTA is rendered inside a scoped ConfigProvider that rounds buttons into
 * pills, so call sites don't each have to remember `shape="round"` -- every
 * empty state in the app gets the app's pill treatment for free, and the ones
 * that already pass `shape="round"` are unaffected.
 */
const EmptyState: React.FC<EmptyStateProps> = ({ icon, title, description, action }) => {
  const { token } = theme.useToken();

  return (
  <div style={{ textAlign: 'center', padding: '32px 16px' }}>
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        background: token.colorFillQuaternary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 12px',
        color: token.colorTextTertiary,
      }}
    >
      {icon}
    </div>
    <div>
      <Text strong style={{ fontSize: 14 }}>
        {title}
      </Text>
    </div>
    <div style={{ marginTop: 4 }}>
      <Text type="secondary" style={{ fontSize: 13 }}>
        {description}
      </Text>
    </div>
    {action && (
      <ConfigProvider theme={{ components: { Button: { borderRadius: 999, borderRadiusLG: 999, borderRadiusSM: 999 } } }}>
        <div style={{ marginTop: 16 }}>{action}</div>
      </ConfigProvider>
    )}
  </div>
  );
};

export default EmptyState;
